import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../../Contexts/TenantContext";
import { productCategoryService } from "../../Api/Services/productCategoryService";
import { ProductCategory } from "../../Models/ProductCategory";
import { toPublicPath } from "../../Utils/publicRoutes";
import { matchListCase } from "../../Utils/DataFormatters";
import { FaChevronDown } from "react-icons/fa";
import { toast } from "react-toastify";

type UserMenuProps = {
  onNavigate?: () => void;
  tone?: "light" | "dark";
  variant?: "default" | "categories";
};

const UserMenu = ({ onNavigate, tone = "light", variant = "default" }: UserMenuProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { tenantPreference } = useTenant();
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const hasCategoryLoadErrorRef = useRef(false);
  const moreDropdownRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      try {
        const data = await productCategoryService.getAll();
        if (isMounted) {
          setCategories(data);
        }
      } catch (error) {
        if (!hasCategoryLoadErrorRef.current) {
          hasCategoryLoadErrorRef.current = true;
          toast.error("Não foi possível carregar as categorias.");
        }
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const closeDropdown = () => setIsDropdownOpen(false);
  const closeMoreDropdown = () => setIsMoreDropdownOpen(false);

  const handleCategoryClick = (categoryId: string) => {
    navigate(`${toPublicPath("/produtos")}?categoria=${categoryId}`);
    closeDropdown();
    onNavigate?.();
  };

  const isActive = (ptPath: string) => {
    const localizedPath = toPublicPath(ptPath);
    return (
      pathname === ptPath ||
      pathname.startsWith(`${ptPath}/`) ||
      pathname === localizedPath ||
      pathname.startsWith(`${localizedPath}/`)
    );
  };

  const desktopToneClass = tone === "dark" ? "text-gray-800 hover:text-gray-600" : "hover:text-gray-300";
  const activeToneClass = tone === "dark" ? "text-gray-900" : "text-gray-300";

  const navItemClass = (path: string) =>
    `py-3 sm:py-0 px-4 sm:px-0 ${desktopToneClass} ${isActive(path) ? activeToneClass : ""}`;

  const defaultMenuLinks = [
    { path: "/", label: "INÍCIO" },
    { path: "/sobre", label: "A EMPRESA" }
  ];

  if (tenantPreference?.enableTechnicalAssistancePage) {
    defaultMenuLinks.push({
      path: "/assistencia-tecnica",
      label: "ASSISTÊNCIA TÉCNICA"
    });
  }

  defaultMenuLinks.push({
    path: "/contato",
    label: "CONTATO"
  });

  const handleNavClick = (path: string, closeMenu?: () => void) => (event: React.MouseEvent) => {
    if (isActive(path)) {
      event.preventDefault();
      navigate(path, { replace: true, state: { refreshKey: Date.now() } });
      closeMenu?.();
      return;
    }
    closeMenu?.();
  };

  const isDesktop = () => typeof window !== "undefined" && window.innerWidth >= 640;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!moreDropdownRef.current) {
        return;
      }

      if (!moreDropdownRef.current.contains(event.target as Node)) {
        closeMoreDropdown();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (variant === "categories") {
    const desktopVisibleLimit = 6;
    const filteredCategories = categories.filter(category => !category.name.toLowerCase().includes("orçamento"));
    const visibleCategories = filteredCategories.slice(0, desktopVisibleLimit);
    const overflowCategories = filteredCategories.slice(desktopVisibleLimit);

    return (
      <nav>
        <ul className="flex flex-col font-bold text-lg sm:text-sm sm:flex-row sm:items-center sm:justify-center sm:gap-11 sm:px-0">
          {visibleCategories.map((category) => (
            <li key={category.id} className="py-3 sm:py-0 px-4 sm:px-0 text-gray-800 hover:text-primary-600">
              <button type="button" onClick={() => handleCategoryClick(category.id)} className="text-left">
                {category.name}
              </button>
            </li>
          ))}

          {overflowCategories.length > 0 && (
            <li
              ref={moreDropdownRef}
              className="relative hidden sm:block text-gray-800"
              onMouseEnter={() => setIsMoreDropdownOpen(true)}
              onMouseLeave={() => setIsMoreDropdownOpen(false)}
            >
              <button
                type="button"
                onClick={() => setIsMoreDropdownOpen((prev) => !prev)}
                className="flex items-center gap-2 hover:text-primary-600"
                aria-expanded={isMoreDropdownOpen}
                aria-haspopup="true"
              >
                {"Mais"}
                <FaChevronDown className={`text-xs transition-transform ${isMoreDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {isMoreDropdownOpen && (
                <div className="absolute left-0 top-full z-50 mt-3 min-w-[220px] rounded-md border border-gray-200 bg-white p-2 shadow-lg">
                  {overflowCategories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      onClick={() => {
                        handleCategoryClick(category.id);
                        closeMoreDropdown();
                      }}
                      className="block w-full rounded px-3 py-2 text-left text-sm text-gray-800 hover:bg-primary-120 hover:text-primary-700"
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              )}
            </li>
          )}

          {overflowCategories.map((category) => (
            <li key={category.id} className="py-3 sm:hidden px-4 text-gray-800 hover:text-primary-600">
              <button type="button" onClick={() => handleCategoryClick(category.id)} className="text-left">
                {category.name}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  return (
      <nav>
        <ul className="flex flex-col font-bold text-lg sm:text-sm sm:flex-row sm:gap-6 sm:px-0">
        {defaultMenuLinks.map((item) => (
          <Fragment key={item.path}>
            <li className={navItemClass(item.path)}>
              <Link to={toPublicPath(item.path)} onClick={handleNavClick(toPublicPath(item.path), onNavigate)}>
                {item.label}
              </Link>
            </li>

            {item.path === "/sobre" && (
              <li
                className={`py-3 sm:py-0 px-4 sm:px-0 relative group ${desktopToneClass} ${isActive("/produtos") ? activeToneClass : ""}`}
                onMouseEnter={() => {
                  if (isDesktop() && categories.length > 0) {
                    setIsDropdownOpen(true);
                  }
                }}
                onMouseLeave={() => {
                  if (isDesktop() && categories.length > 0) {
                    setIsDropdownOpen(false);
                  }
                }}
              >
                {/* Desktop trigger */}
                <Link
                  to={toPublicPath("/produtos")}
                  className="hidden sm:flex items-center gap-2"
                  onClick={handleNavClick(toPublicPath("/produtos"), closeDropdown)}
                  onMouseEnter={() => {
                    if (isDesktop() && categories.length > 0) {
                      setIsDropdownOpen(true);
                    }
                  }}
                  aria-haspopup="true"
                  aria-expanded={isDropdownOpen}
                >
                  {"PRODUTOS"}
                  <FaChevronDown className={`text-xs transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                </Link>

                {/* Mobile trigger */}
                <button
                  type="button"
                  className="flex sm:hidden items-center gap-2 w-full"
                  onClick={() => setIsDropdownOpen(prev => !prev)}
                  aria-haspopup="true"
                  aria-expanded={isDropdownOpen}
                >
                  {"PRODUTOS"}
                  <FaChevronDown className={`text-xs transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {/* Hover bridge for desktop */}
                {categories.length > 0 && (
                  <div
                    className="pointer-events-auto absolute left-0 top-full h-4 w-24 hidden sm:block"
                    onMouseEnter={(e) => {
                      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                      const withinTrigger = e.clientX <= rect.left + 30;
                      if (isDesktop() && withinTrigger) {
                        setIsDropdownOpen(true);
                      }
                    }}
                    onMouseLeave={() => {
                      if (isDesktop()) {
                        setIsDropdownOpen(false);
                      }
                    }}
                  />
                )}

                {/* Desktop dropdown */}
                {isDropdownOpen && categories.length > 0 && (
                  <div
                    className="absolute left-0 top-full mt-3 w-64 rounded border border-gray-200 bg-white text-gray-800 shadow-lg z-50 hidden sm:block"
                    onMouseEnter={() => {
                      if (isDesktop()) {
                        setIsDropdownOpen(true);
                      }
                    }}
                    onMouseLeave={() => {
                      if (isDesktop()) {
                        setIsDropdownOpen(false);
                      }
                    }}
                  >
                    <div className="absolute -top-2 left-8 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-200" />
                    <ul className="flex flex-col divide-y divide-gray-100 pt-2">
                      {categories.map(category => (
                        <li key={category.id}>
                          <button
                            type="button"
                            onClick={() => handleCategoryClick(category.id)}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-primary-120"
                          >
                            {category.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Mobile dropdown */}
                {isDropdownOpen && categories.length > 0 && (
                  <div className="sm:hidden mt-2 ml-4 space-y-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (isActive("/produtos")) {
                          window.location.reload();
                        } else {
                          navigate(toPublicPath("/produtos"));
                        }
                        closeDropdown();
                        onNavigate?.();
                      }}
                      className="block w-full text-left px-2 py-2 text-sm text-primary-600 bg-white rounded border border-gray-200 font-semibold transition-colors hover:bg-primary-120"
                    >
                      {matchListCase("Todos os produtos", categories.map((category) => category.name))}
                    </button>
                    {categories.map(category => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleCategoryClick(category.id)}
                        className="block w-full text-left px-2 py-2 text-sm text-primary-600 bg-white rounded border border-gray-200 transition-colors hover:bg-primary-120"
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            )}
          </Fragment>
        ))}

      </ul>
    </nav>
  );
};

export default UserMenu;
